import type { PropertyResponse } from '@/shared/api';
import { cn } from '@/shared/lib/cn';
import {
  isFiltered,
  type BookingsFilterState,
  type SortState,
} from '../components/bookingsListSearch';

export interface FilterControlsProps {
  filters: BookingsFilterState;
  onFiltersChange: (next: BookingsFilterState) => void;
  query: string;
  sort: SortState;
  onSortChange: (next: SortState) => void;
  onReset: () => void;
  canSeeGuests: boolean;
  properties: PropertyResponse[];
}

const SORTS: Array<[string, string]> = [
  ['checkInDate:asc', 'Anreise ↑'],
  ['checkInDate:desc', 'Anreise ↓'],
  ['totalPrice:desc', 'Betrag ↓'],
  ['totalPrice:asc', 'Betrag ↑'],
  ['status:asc', 'Status A–Z'],
  ['status:desc', 'Status Z–A'],
  ['channel:asc', 'Kanal A–Z'],
  ['channel:desc', 'Kanal Z–A'],
  ['property:asc', 'Apartment A–Z'],
  ['property:desc', 'Apartment Z–A'],
  ['guest:asc', 'Gast A–Z'],
  ['guest:desc', 'Gast Z–A'],
];

const pill =
  'border-hairline rounded-pill flex h-[46px] items-center gap-2 border bg-white px-4 text-[13px]';

/** Apartment, Anreise von–bis, „Vergangene zeigen“, Sortierung und Zurücksetzen. */
export function BookingsFilterControls(props: FilterControlsProps) {
  const { filters, onFiltersChange, query, sort, onSortChange, canSeeGuests } = props;
  const set = (patch: Partial<BookingsFilterState>) => onFiltersChange({ ...filters, ...patch });
  return (
    <>
      <select
        value={filters.propertyId}
        onChange={(event) => set({ propertyId: event.target.value })}
        aria-label="Apartment-Filter"
        className={cn(pill, 'font-semibold')}
      >
        <option value="">Alle Apartments</option>
        {/* Ein gewähltes Apartment bleibt sichtbar, auch wenn seine Liste noch fehlt. */}
        {filters.propertyId && !props.properties.some((p) => p.id === filters.propertyId) && (
          <option value={filters.propertyId}>Gewähltes Apartment</option>
        )}
        {props.properties.map((p) => (
          <option key={p.id} value={p.id}>
            {p.adminDisplayName ?? p.name}
          </option>
        ))}
      </select>
      <span className={cn(pill, 'max-w-full flex-wrap')}>
        <span className="text-ink-soft">Anreise</span>
        <input
          type="date"
          value={filters.dateFrom}
          onChange={(e) => set({ dateFrom: e.target.value })}
          aria-label="Anreise frühestens"
          className="w-[124px] min-w-0 bg-transparent font-bold"
        />
        <span className="text-ink-faint">–</span>
        <input
          type="date"
          value={filters.dateTo}
          onChange={(e) => set({ dateTo: e.target.value })}
          aria-label="Anreise spätestens"
          className="w-[124px] min-w-0 bg-transparent font-bold"
        />
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={filters.showPast}
        onClick={() => set({ showPast: !filters.showPast })}
        className={cn(pill, 'pl-2 font-semibold')}
      >
        <span
          className={cn(
            'rounded-pill relative h-[22px] w-9 flex-none',
            filters.showPast ? 'bg-forest' : 'bg-line-strong',
          )}
        >
          <span
            className={cn(
              'rounded-pill absolute top-[3px] size-4 bg-white transition-[left]',
              filters.showPast ? 'left-[17px]' : 'left-[3px]',
            )}
          />
        </span>
        Vergangene zeigen
      </button>
      <select
        value={`${sort.field}:${sort.direction}`}
        onChange={(event) => {
          const [field, direction] = event.target.value.split(':') as [
            SortState['field'],
            SortState['direction'],
          ];
          onSortChange({ field, direction });
        }}
        aria-label="Sortierung"
        className="bg-card-alt rounded-pill h-[46px] px-4 text-[13px] font-semibold"
      >
        {SORTS.filter(([value]) => canSeeGuests || !value.startsWith('guest')).map(
          ([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ),
        )}
      </select>
      {(isFiltered(filters, query) || filters.showPast) && (
        <button
          type="button"
          onClick={props.onReset}
          className="text-sealwax h-[46px] px-3 text-[13px] font-bold"
        >
          Filter zurücksetzen
        </button>
      )}
    </>
  );
}
